import { metaDescription } from '@/seo/routes';
import { Link, useParams } from 'react-router-dom';
import { AsaasBaasSeal } from '@/components/AsaasBaasSeal';
import { Breadcrumbs } from '@/components/site/Breadcrumbs';
import { Prose } from '@/components/site/Prose';
import { SiteMeta } from '@/components/site/SiteMeta';
import { appBadgeLabel, appPriceLabel, appPublicName, appTiers, appUsageHint, type PublicApp } from '@/content/apps';
import { TierComparison } from '@/components/site/TierComparison';
import { sectors } from '@/content/sectors';
import { trialHref } from '@/content/plans';
import { NotFoundPage } from '@/pages/site/seo/NotFoundPage';
import { appPath, appWiki, buildApps, findBuildApp } from '@/seo/catalog';

const CATEGORY_LABEL: Record<string, string> = {
  health: 'Saúde',
  operation: 'Operação',
  finance: 'Financeiro',
  fiscal: 'Fiscal',
  automation: 'Automação e integração',
  channel: 'Canais',
  relationship: 'Relacionamento',
  semantic: 'Adaptação ao setor',
};

function relatedApps(app: PublicApp): PublicApp[] {
  const mesmoSetor = new Set<string>();
  for (const sector of sectors) {
    if (!sector.apps.includes(app.slug)) continue;
    for (const slug of sector.apps) if (slug !== app.slug) mesmoSetor.add(slug);
  }
  const porSetor = buildApps.filter((a) => mesmoSetor.has(a.slug));
  const porCategoria = buildApps.filter((a) => a.category === app.category && a.slug !== app.slug);
  const vistos = new Set<string>();
  return [...porSetor, ...porCategoria]
    .filter((a) => (vistos.has(a.slug) ? false : (vistos.add(a.slug), true)))
    .slice(0, 4);
}

export function AppDetailPage() {
  const { slug = '' } = useParams();
  const app = findBuildApp(slug);
  if (!app) return <NotFoundPage />;

  const wiki = appWiki(app.slug);
  const price = appPriceLabel(app);
  const hint = appUsageHint(app);
  const soon = app.tier === 'soon' || app.availability === 'coming_soon';
  const usadoEm = sectors.filter((s) => s.apps.includes(app.slug));
  const relacionados = relatedApps(app);
  // App que movimenta dinheiro precisa dizer, na propria pagina, quem presta o
  // servico financeiro. Ate aqui a pagina do Pix descrevia a tarifa sem citar
  // o Asaas em lugar nenhum, e a analise de BaaS parou nesse ponto.
  const movimentaDinheiro = app.slug === 'payments';

  return (
    <>
      <SiteMeta title={`${appPublicName(app)} | Operax`} description={metaDescription(app.description ?? app.name)} path={appPath(app.slug)} />

      <section className="page-hero">
        <div className="hero-grid-bg" />
        <div className="shell page-hero-in rev">
          <Breadcrumbs
            items={[
              { name: 'Início', path: '/' },
              { name: 'Apps', path: '/apps' },
              { name: appPublicName(app) },
            ]}
          />
          <div className="eyebrow">
            App da Operax · {CATEGORY_LABEL[app.category] ?? app.category}
          </div>
          <h1>{appPublicName(app)}</h1>
          <p className="lead">{wiki?.subtitle || app.description}</p>

          <dl className="seo-facts">
            <div>
              <dt>Situação</dt>
              <dd>{soon ? 'Em breve' : appBadgeLabel(app.tier)}</dd>
            </div>
            <div>
              <dt>Preço</dt>
              <dd>
                {price ?? (soon ? 'A definir' : 'Sob consulta')}
                {hint ? <small>{hint}</small> : null}
              </dd>
            </div>
            <div>
              <dt>Como contrata</dt>
              <dd>{app.tier === 'included' ? 'Já vem no plano' : 'Ativa no painel, mês a mês'}</dd>
            </div>
          </dl>

          {!soon && (
            <div className="hero-cta" style={{ marginTop: 28 }}>
              <a className="btn btn-primary" href={trialHref()}>
                Assinar a Operax
              </a>
              <Link className="btn btn-ghost" to="/precos">
                Ver planos
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="pad">
        <div className="shell seo-article">
          {wiki?.subtitle && app.description ? <p className="seo-summary">{app.description}</p> : null}
          {wiki ? <Prose markdown={wiki.markdown} /> : <p className="seo-summary">{app.description}</p>}

          {appTiers(app).length > 0 && (
            <section className="seo-tiers" aria-labelledby="niveis-do-app">
              <h2 id="niveis-do-app">Níveis e preços</h2>
              <p>
                {app.name} tem {appTiers(app).length} níveis. Você escolhe na hora de contratar e pode trocar depois, em
                Apps e recursos.
              </p>
              <TierComparison app={app} />
              <p>
                <Link className="btn btn-ghost" to="/calculadora">
                  Montar meu pacote
                </Link>
              </p>
            </section>
          )}

          {movimentaDinheiro && (
            <AsaasBaasSeal surface="landing_app_payments" className="mt-8" />
          )}

          {usadoEm.length > 0 && (
            <aside className="seo-aside">
              <h2>Setores que usam este app</h2>
              <ul>
                {usadoEm.map((sector) => (
                  <li key={sector.slug}>
                    <Link to={`/setores/${sector.slug}`}>{sector.name}</Link>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </section>

      {relacionados.length > 0 && (
        <section className="pad seo-soft">
          <div className="shell">
            <div className="sec-head rev">
              <div className="eyebrow">Combina com</div>
              <h2 className="h2">Apps que costumam andar junto.</h2>
            </div>
            <div className="apps-grid" style={{ marginTop: 32 }}>
              {relacionados.map((rel) => (
                <Link key={rel.slug} className="app-card rev" to={appPath(rel.slug)}>
                  <div className="nm">{rel.name}</div>
                  <div className="ds">{rel.description ?? ''}</div>
                  <div className="app-price">{appPriceLabel(rel) ?? 'Em breve'}</div>
                  <span className="app-more">
                    Ver detalhes <span aria-hidden>→</span>
                  </span>
                </Link>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 36 }}>
              <Link className="btn btn-ghost" to="/apps">
                Ver os {buildApps.length} apps do catálogo
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
